'use client';

import React, { useState } from 'react';
import { Building2, Package, Briefcase, X, User, Sparkles, Check, HelpCircle, Truck } from 'lucide-react';
import { authService } from '@/lib/authService';
import {
  type ExpenseAccountOption,
  type AllocationType,
  getAllowedAllocationTypes,
  ALL_ALLOCATION_OPTIONS,
} from '@/lib/finance/expenseAccountMapping';
import type { InventoryItemMatch } from '@/lib/finance/matching/aiMatcher';
import { SearchableAccountSelect } from '@/components/shared/finance/SearchableAccountSelect';
import { AsyncSearchableSelect } from '@/components/shared/finance/AsyncSearchableSelect';

export interface ExpenseLineItemRowProps {
  /** Current expense account code */
  accountCode: string;
  /** Current allocation type */
  allocationType: AllocationType;
  /** Linked customer ID (for job allocation) */
  linkedCustomerId: string;
  /** Linked customer name (for display when already selected) */
  linkedCustomerName?: string;
  /** Linked job ID (for job allocation) */
  linkedJobId: string;
  /** Linked job display name (e.g. "JOB-001 — Title") */
  linkedJobName?: string;
  /** Linked inventory item ID (for stock allocation) */
  linkedInventoryItemId: string;
  /** Linked inventory item name (for display when already selected) */
  linkedInventoryItemName?: string;
  /** Linked equipment item ID (for equipment allocation) */
  linkedEquipmentItemId: string;
  /** Linked equipment item name (for display when already selected) */
  linkedEquipmentItemName?: string;
  /** Linked vehicle ID (for vehicle allocation) */
  linkedVehicleId: string;
  /** Linked vehicle registration/name (for display when already selected) */
  linkedVehicleName?: string;
  /** Available expense accounts */
  expenseAccounts: ExpenseAccountOption[];
  /** Equipment options for equipment allocation */
  equipmentOptions: { id: string; title: string; item_number?: string | null }[];
  /** Whether the row is disabled (e.g. not in edit mode) */
  disabled: boolean;
  /** Dark mode flag */
  isDarkMode: boolean;
  /** Line item index (for unique IDs) */
  index: number;
  /** Callback when GL account changes */
  onAccountChange: (accountCode: string) => void;
  /** Callback when allocation type changes */
  onAllocationTypeChange: (allocationType: AllocationType) => void;
  /** Callback when a field value changes */
  onFieldChange: (field: string, value: string) => void;
  /** Whether inventory tracking is enabled (affects stock allocation visibility) */
  inventoryTrackingEnabled?: boolean;
  /** Whether this line item is billable to a job */
  isBillable?: boolean;
  /** Job ID to bill this line item to */
  billableToJobId?: string;
  /** Markup percentage for billable items */
  markupPercentage?: number;
  /** Whether the document is in pending status (controls editability) */
  isPending?: boolean;
  /** Customer name for display (when billable) */
  billableCustomerName?: string;
  /** AI per-line categorization suggestion */
  aiSuggestion?: {
    account_code: string;
    account_name: string;
    confidence: number;
    reasons: string[];
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
  } | null;
  /** Whether AI suggestions are currently loading */
  aiSuggestionsLoading?: boolean;
  /** Callback to apply AI categorization (GL only) */
  onApplyAICategorization?: (accountCode: string, lineIndex: number) => void;
  /** Callback to apply full allocation chain */
  onApplyFullAllocation?: (lineIndex: number, suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
  }) => void;
  /** Whether the GL has already been applied from the AI suggestion */
  glApplied?: boolean;
  /** Line item index (for AI apply callbacks) */
  lineIndex?: number;
}

export default function ExpenseLineItemRow({
  accountCode,
  allocationType,
  linkedCustomerId,
  linkedCustomerName,
  linkedJobId,
  linkedJobName,
  linkedInventoryItemId,
  linkedInventoryItemName,
  linkedEquipmentItemId,
  linkedEquipmentItemName,
  linkedVehicleId,
  linkedVehicleName,
  expenseAccounts,
  equipmentOptions,
  disabled,
  isDarkMode,
  index,
  onAccountChange,
  onAllocationTypeChange,
  onFieldChange,
  isBillable = false,
  billableToJobId = '',
  markupPercentage = 0,
  isPending = true,
  billableCustomerName,
  aiSuggestion = null,
  aiSuggestionsLoading = false,
  onApplyAICategorization,
  onApplyFullAllocation,
  glApplied = false,
  lineIndex = 0,
}: ExpenseLineItemRowProps) {
  const [billableJobsCache, setBillableJobsCache] = useState<Array<{ id: string; item_number: string; title: string; status: string }>>([]);
  const [inventoryItemsCache, setInventoryItemsCache] = useState<Array<{ id: string; title: string; sku?: string | null; item_number?: string | null }>>([]);
  const [customersCache, setCustomersCache] = useState<Array<{ id: string; company_name: string | null; contact_person: string | null }>>([]);
  const [jobsCache, setJobsCache] = useState<Array<{ id: string; item_number: string; title: string; status: string }>>([]);
  const [equipmentItemsCache, setEquipmentItemsCache] = useState<Array<{ id: string; title: string; item_number?: string | null }>>([]);
  const [vehiclesCache, setVehiclesCache] = useState<Array<{ id: string; title: string; registration?: string | null }>>([]);

  // ── Cascading state ──────────────────────────────────────────────
  // Level 0: GL account selected
  const glSelected = Boolean(accountCode);
  // Level 1: Allocation type defined (requires GL first)
  // Admin can override to ANY allocation type regardless of GL account subtype,
  // so the dropdown shows all options. The allowed list is only used for
  // auto-deriving the default when a GL is first selected.
  const allowedAllocationTypes = accountCode
    ? getAllowedAllocationTypes(expenseAccounts, accountCode)
    : [];
  const allocationOptions = ALL_ALLOCATION_OPTIONS;
  // Allocation type dropdown is disabled only when the row itself is disabled
  // (e.g. not in edit mode). It is no longer gated on GL selection so users
  // can switch from 'job' to 'overhead' (general) without first picking a GL
  // account — this is critical for DINs that arrive with allocation_type='job'
  // but no GL, where the user needs to switch to 'overhead' to avoid the
  // job-link completion gate.
  const isAllocationTypeDisabled = disabled;
  // Whether the current allocation type needs no drill-down (overhead/suspense)
  const hasNoDrillDown = allocationType === 'overhead' || allocationType === 'suspense';
  // Level 2: Whether the current allocation type requires a link selection
  const needsLink = allocationType === 'job' || allocationType === 'stock' || allocationType === 'equipment' || allocationType === 'vehicle';
  // Level 2 selected: a customer / inventory / equipment / vehicle link has been chosen
  const linkSelected = Boolean(linkedCustomerId || linkedInventoryItemId || linkedEquipmentItemId || linkedVehicleId);
  // Level 3 (job only): customer selected → job dropdown becomes usable
  const customerSelected = Boolean(linkedCustomerId);

  const disabledColClass = isDarkMode
    ? 'border-gray-700 bg-gray-800/30 text-gray-600'
    : 'border-gray-200 bg-gray-50 text-gray-400';

  /** Render a disabled placeholder cell with an icon + message */
  const renderDisabledPlaceholder = (icon: React.ReactNode, message: string) => (
    <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs ${disabledColClass}`}>
      {icon}
      <span>{message}</span>
    </div>
  );

  // Compute AI suggestion state for compact display
  const aiAllocLabel = aiSuggestion?.allocation_type
    ? aiSuggestion.allocation_type.charAt(0).toUpperCase() + aiSuggestion.allocation_type.slice(1)
    : null;
  const aiHasJobLink = aiSuggestion?.allocation_type === 'job'
    && (aiSuggestion.suggested_customer_name || aiSuggestion.suggested_job_name);
  const aiHasStockLink = aiSuggestion?.allocation_type === 'stock'
    && aiSuggestion.inventory_item_match;
  const aiHasFullChain = Boolean(aiAllocLabel && (aiHasJobLink || aiHasStockLink || aiSuggestion?.allocation_type === 'overhead' || aiSuggestion?.allocation_type === 'suspense'));
  const aiFullChainApplied = glApplied
    && (!aiSuggestion?.suggested_customer_id || linkedCustomerId === aiSuggestion.suggested_customer_id)
    && (!aiSuggestion?.suggested_job_id || linkedJobId === aiSuggestion.suggested_job_id)
    && (!aiSuggestion?.inventory_item_match || linkedInventoryItemId === aiSuggestion.inventory_item_match.item_id);

  return (
    <div className={`grid grid-cols-5 gap-2 items-start px-2 py-1.5 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-100 bg-gray-50/50'}`}>
      {/* ── Col 0: AI Suggestion (compact, left of GL) ── */}
      <div className="flex items-center gap-1 flex-wrap min-h-[34px]">
        {aiSuggestionsLoading && !aiSuggestion && (
          <div className={`flex items-center gap-1 text-[10px] ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
            <Sparkles className="h-3 w-3 animate-pulse" />
            <span>AI...</span>
          </div>
        )}
        {aiSuggestion && aiSuggestion.account_code && (
          <>
            <Sparkles className="h-3 w-3 text-purple-500 flex-shrink-0" />
            <span className={`text-[10px] truncate max-w-[120px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} title={aiSuggestion.account_name}>
              {aiSuggestion.account_name}
            </span>
            <span className={`text-[10px] font-mono shrink-0 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
              {aiSuggestion.account_code}
            </span>
            {(aiSuggestion.reasons?.length ?? 0) > 0 && (
              <span title={aiSuggestion.reasons.join(' • ')} className="inline-flex">
                <HelpCircle className={`h-3 w-3 cursor-help ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} />
              </span>
            )}
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${aiSuggestion.confidence >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : aiSuggestion.confidence >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
              {Math.round(aiSuggestion.confidence)}%
            </span>
            {/* Apply All — full chain */}
            {onApplyFullAllocation && aiHasFullChain && !aiFullChainApplied && (
              <button
                type="button"
                onClick={() => onApplyFullAllocation(lineIndex, {
                  account_code: aiSuggestion.account_code,
                  allocation_type: aiSuggestion.allocation_type,
                  inventory_item_match: aiSuggestion.inventory_item_match,
                  suggested_customer_id: aiSuggestion.suggested_customer_id,
                  suggested_customer_name: aiSuggestion.suggested_customer_name,
                  suggested_job_id: aiSuggestion.suggested_job_id,
                  suggested_job_name: aiSuggestion.suggested_job_name,
                })}
                className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                title={`Apply full allocation: ${aiSuggestion.account_code} → ${aiAllocLabel}${aiHasJobLink ? ` → ${aiSuggestion.suggested_customer_name || ''}${aiSuggestion.suggested_job_name ? ` → ${aiSuggestion.suggested_job_name}` : ''}` : ''}${aiHasStockLink ? ` → ${aiSuggestion.inventory_item_match?.item_title}` : ''}`}
              >
                <Check className="h-2.5 w-2.5" />
                Apply All
              </button>
            )}
            {/* Fallback: Apply GL only */}
            {onApplyAICategorization && !glApplied && !(onApplyFullAllocation && aiHasFullChain) && (
              <button
                type="button"
                onClick={() => onApplyAICategorization(aiSuggestion.account_code, lineIndex)}
                className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium transition-colors ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                title={`Apply ${aiSuggestion.account_code} to this line`}
              >
                <Check className="h-2.5 w-2.5" />
                Apply
              </button>
            )}
            {aiFullChainApplied && (
              <span className={`text-[9px] font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                ✓ Applied
              </span>
            )}
            {glApplied && !aiFullChainApplied && onApplyFullAllocation && aiHasFullChain && (
              <span className={`text-[9px] ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                links pending
              </span>
            )}
          </>
        )}
        {!aiSuggestion && !aiSuggestionsLoading && (
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>—</span>
        )}
      </div>

      {/* ── Col 1: GL Account (always enabled) ── */}
      <div>
        <SearchableAccountSelect
          accounts={expenseAccounts}
          value={accountCode}
          onChange={onAccountChange}
          disabled={disabled}
          isDark={isDarkMode}
          placeholder="- GL account -"
          recentKey={`expense-row-${index}`}
          className="text-xs"
        />
      </div>

      {/* ── Col 2: Allocation Type (disabled until GL selected) ── */}
      <div>
        <select
          value={glSelected ? allocationType : ''}
          onChange={(e) => onAllocationTypeChange(e.target.value as AllocationType)}
          disabled={isAllocationTypeDisabled}
          className={`w-full rounded-lg border px-2 py-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
        >
          {!glSelected && <option value="">Select allocation…</option>}
          {allocationOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* ── Col 3: First conditional dropdown (disabled until allocation type defined) ── */}
      {!glSelected ? (
        renderDisabledPlaceholder(<Building2 className="w-3 h-3 opacity-40" />, 'Select GL first')
      ) : hasNoDrillDown || !needsLink ? (
        renderDisabledPlaceholder(
          allocationType === 'suspense' ? <Briefcase className="w-3 h-3 opacity-40" /> : <Briefcase className="w-3 h-3 opacity-40" />,
          allocationType === 'suspense' ? 'Suspense — no link required' : 'No link required',
        )
      ) : allocationType === 'job' ? (
        <div>
          <AsyncSearchableSelect
            value={linkedCustomerId}
            onChange={(val) => {
              onFieldChange('linked_customer_id', val);
              const matched = customersCache.find((c) => c.id === val);
              onFieldChange('linked_customer_name', matched?.company_name || matched?.contact_person || '');
              onFieldChange('linked_job_id', '');
              onFieldChange('linked_job_name', '');
            }}
            onSearch={async (term) => {
              const token = authService.getTokens()?.access_token;
              if (!token) return [];
              const params = new URLSearchParams({ is_active: 'true', limit: '50', contact_types: 'customer' });
              if (term.trim()) params.set('search', term);
              const res = await fetch(`/api/v1/contacts?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) return [];
              const data = await res.json();
              const contacts = (data.data || []) as Array<Record<string, unknown>>;
              setCustomersCache(contacts.map((c) => ({
                id: String(c.id || ''),
                company_name: (c.company_name as string | null) ?? null,
                contact_person: (c.contact_person as string | null) ?? null,
              })));
              return contacts.map((c: Record<string, unknown>) => ({
                value: String(c.id || ''),
                label: String(c.company_name || c.contact_person || `Contact ${c.id}`),
                description: [c.contact_person, c.email].filter(Boolean).map(String).join(' · ') || undefined,
                icon: <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
              }));
            }}
            disabled={disabled}
            isDark={isDarkMode}
            placeholder="Search customer..."
            searchPlaceholder="Search customers..."
            emptyMessage="No customers found"
            idleMessage="Start typing to search customers..."
            idPrefix={`cust-${index}`}
            loadOnOpen
            selectedDisplay={linkedCustomerName || undefined}
            selectedIcon={<Building2 className="w-4 h-4 shrink-0 text-blue-600" />}
            className="!px-2 !py-1.5"
          />
        </div>
      ) : allocationType === 'stock' ? (
        <div>
          <AsyncSearchableSelect
            value={linkedInventoryItemId}
            onChange={(val) => {
              onFieldChange('linked_inventory_item_id', val);
              const matched = inventoryItemsCache.find((i) => i.id === val);
              onFieldChange('linked_inventory_name', matched?.title || '');
              if (matched?.sku) {
                onFieldChange('linked_inventory_sku', matched.sku);
              }
            }}
            onSearch={async (term) => {
              const token = authService.getTokens()?.access_token;
              if (!token) return [];
              const params = new URLSearchParams({ item_type: 'inventory', page: '1', limit: '50' });
              if (term.trim()) params.set('search', term);
              const res = await fetch(`/api/v1/work-items?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) return [];
              const data = await res.json();
              const items = (data.data || []) as Array<Record<string, unknown>>;
              setInventoryItemsCache(items.map((item) => ({
                id: String(item.id || ''),
                title: String(item.title || item.name || ''),
                sku: (item.sku as string | null) ?? null,
                item_number: (item.item_number as string | null) ?? null,
              })));
              return items.map((item: Record<string, unknown>) => ({
                value: String(item.id || ''),
                label: String(item.title || item.name || ''),
                description: [item.sku, item.item_number].filter(Boolean).map(String).join(' · ') || undefined,
                icon: <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
              }));
            }}
            disabled={disabled}
            isDark={isDarkMode}
            placeholder="Search inventory item..."
            searchPlaceholder="Search inventory..."
            emptyMessage="No inventory items found"
            idleMessage="Start typing to search inventory..."
            idPrefix={`inv-${index}`}
            loadOnOpen
            selectedDisplay={linkedInventoryItemName || undefined}
            selectedIcon={<Package className="w-4 h-4 shrink-0 text-blue-600" />}
            className="!px-2 !py-1.5"
          />
        </div>
      ) : allocationType === 'equipment' ? (
        <div>
          <AsyncSearchableSelect
            value={linkedEquipmentItemId}
            onChange={(val) => {
              onFieldChange('linked_equipment_item_id', val);
              const matched = equipmentItemsCache.find((e) => e.id === val);
              onFieldChange('linked_equipment_name', matched?.title || '');
            }}
            onSearch={async (term) => {
              const token = authService.getTokens()?.access_token;
              if (!token) return [];
              const params = new URLSearchParams({ item_type: 'equipment', page: '1', limit: '50' });
              if (term.trim()) params.set('search', term);
              const res = await fetch(`/api/v1/work-items?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) return [];
              const data = await res.json();
              const items = (data.data || []) as Array<Record<string, unknown>>;
              setEquipmentItemsCache(items.map((item) => ({
                id: String(item.id || ''),
                title: String(item.title || item.name || ''),
                item_number: (item.item_number as string | null) ?? null,
              })));
              return items.map((item: Record<string, unknown>) => ({
                value: String(item.id || ''),
                label: String(item.title || item.name || ''),
                description: [item.item_number].filter(Boolean).map(String).join(' · ') || undefined,
                icon: <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
              }));
            }}
            disabled={disabled}
            isDark={isDarkMode}
            placeholder="Search equipment..."
            searchPlaceholder="Search equipment..."
            emptyMessage="No equipment found"
            idleMessage="Start typing to search equipment..."
            idPrefix={`equip-${index}`}
            loadOnOpen
            selectedDisplay={linkedEquipmentItemName || undefined}
            selectedIcon={<Briefcase className="w-4 h-4 shrink-0 text-blue-600" />}
            className="!px-2 !py-1.5"
          />
        </div>
      ) : allocationType === 'vehicle' ? (
        <div>
          <AsyncSearchableSelect
            value={linkedVehicleId}
            onChange={(val) => {
              onFieldChange('linked_vehicle_id', val);
              const matched = vehiclesCache.find((v) => v.id === val);
              onFieldChange('linked_vehicle_name', matched?.title || matched?.registration || '');
            }}
            onSearch={async (term) => {
              const token = authService.getTokens()?.access_token;
              if (!token) return [];
              const params = new URLSearchParams({ item_type: 'asset', item_subtype: 'vehicle', page: '1', limit: '50' });
              if (term.trim()) params.set('search', term);
              const res = await fetch(`/api/v1/work-items?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) return [];
              const data = await res.json();
              const items = (data.data || []) as Array<Record<string, unknown>>;
              setVehiclesCache(items.map((item) => ({
                id: String(item.id || ''),
                title: String(item.title || item.name || ''),
                registration: (item.registration as string | null) ?? null,
              })));
              return items.map((item: Record<string, unknown>) => ({
                value: String(item.id || ''),
                label: String(item.title || item.name || item.registration || ''),
                description: [item.registration, item.item_number].filter(Boolean).map(String).join(' · ') || undefined,
                icon: <Truck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
              }));
            }}
            disabled={disabled}
            isDark={isDarkMode}
            placeholder="Search vehicles..."
            searchPlaceholder="Search vehicles..."
            emptyMessage="No vehicles found"
            idleMessage="Start typing to search vehicles..."
            idPrefix={`veh-${index}`}
            loadOnOpen
            selectedDisplay={linkedVehicleName || undefined}
            selectedIcon={<Truck className="w-4 h-4 shrink-0 text-blue-600" />}
            className="!px-2 !py-1.5"
          />
        </div>
      ) : (
        renderDisabledPlaceholder(<Briefcase className="w-3 h-3 opacity-40" />, 'No link required')
      )}

      {/* ── Col 4: Second conditional dropdown (job selector — disabled until customer selected) ── */}
      {allocationType === 'job' && glSelected ? (
        <div>
          {customerSelected ? (
            <AsyncSearchableSelect
              value={linkedJobId}
              onChange={(val) => {
                onFieldChange('linked_job_id', val);
                const matched = jobsCache.find((j) => j.id === val);
                onFieldChange('linked_job_name', matched ? `${matched.item_number} — ${matched.title}` : '');
              }}
              onSearch={async (term) => {
                const token = authService.getTokens()?.access_token;
                if (!token) return [];
                const params = new URLSearchParams({
                  item_type: 'job',
                  status: 'active,in_progress,open,pending',
                  limit: '200',
                  contact_id: linkedCustomerId,
                });
                if (term.trim()) params.set('search', term);
                const res = await fetch(`/api/v1/work-items?${params}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return [];
                const data = await res.json();
                const jobs = (data.data || []) as Array<Record<string, unknown>>;
                setJobsCache(jobs.map((j) => ({
                  id: String(j.id || ''),
                  item_number: String(j.item_number || ''),
                  title: String(j.title || ''),
                  status: String(j.status || 'active'),
                })));
                return jobs.map((j: Record<string, unknown>) => ({
                  value: String(j.id || ''),
                  label: `${j.item_number || ''} — ${j.title || ''}`,
                  description: String(j.status || ''),
                  icon: <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                }));
              }}
              disabled={disabled}
              isDark={isDarkMode}
              placeholder="Search job..."
              searchPlaceholder="Search jobs..."
              emptyMessage="No jobs found for this customer"
              idleMessage="Start typing to search jobs..."
              idPrefix={`job-${index}`}
              loadOnOpen
              selectedDisplay={linkedJobName || undefined}
              selectedIcon={<Briefcase className="w-4 h-4 shrink-0 text-green-600" />}
              className="!px-2 !py-1.5"
            />
          ) : (
            renderDisabledPlaceholder(<Building2 className="w-3 h-3 opacity-40" />, 'Select customer first')
          )}
        </div>
      ) : !glSelected ? (
        renderDisabledPlaceholder(<Briefcase className="w-3 h-3 opacity-40" />, 'Select GL first')
      ) : needsLink && !linkSelected ? (
        renderDisabledPlaceholder(<Briefcase className="w-3 h-3 opacity-40" />, 'Select link first')
      ) : (
        renderDisabledPlaceholder(<Briefcase className="w-3 h-3 opacity-40" />, 'No link required')
      )}

      {/* Billable toggle + markup — shown whenever the GL account permits job
          allocation (or the line already is billable / job-allocated). QBO
          parity: the customer/job link is only compulsory while Billable is
          checked; checking it surfaces the customer → job selectors. */}
      {(isBillable || allocationType === 'job' || allowedAllocationTypes.includes('job')) && (
        <div className={`col-span-4 mt-2 flex flex-wrap items-center gap-4 border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title="Mark this line as recoverable from a customer/job. Billable lines must be linked to a customer and job before processing; non-billable lines only need a GL account.">
            <input
              type="checkbox"
              checked={isBillable}
              disabled={!isPending}
              onChange={(e) => {
                const checked = e.target.checked;
                onFieldChange('is_billable', checked ? 'true' : 'false');
                if (checked) {
                  // Billable lines must resolve to a customer:job — surface the
                  // link selectors by switching to job allocation.
                  if (allocationType !== 'job') {
                    onAllocationTypeChange('job');
                  }
                  if (linkedJobId && !billableToJobId) {
                    onFieldChange('billable_to_job_id', linkedJobId);
                  }
                }
                // Unchecking only clears the flag — the customer/job link is
                // preserved as optional provenance and never auto-removed.
              }}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
            />
            <span className="flex items-center gap-1">
              Billable
              <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[8px] font-bold ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`} title="Mark this line as recoverable from a customer/job. Billable lines must be linked to a customer and job before processing; non-billable lines only need a GL account.">?</span>
            </span>
          </label>
          {isBillable && billableCustomerName && (
            <span className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <User className="h-3 w-3" />
              {billableCustomerName}
            </span>
          )}
          {isBillable && (
            <>
              <div className="flex-1 min-w-[180px]">
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Billable to Job
                </label>
                {allocationType === 'job' && linkedJobId ? (
                  <div className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}>
                    <Briefcase className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                    <span className="truncate">
                      {(() => {
                        const job = billableJobsCache.find(j => j.id === billableToJobId);
                        return job ? `${job.item_number} — ${job.title}` : billableToJobId || linkedJobId;
                      })()}
                    </span>
                    {isPending && (
                      <button
                        type="button"
                        onClick={() => onFieldChange('billable_to_job_id', '')}
                        className={`ml-auto rounded p-0.5 ${isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-100'}`}
                        title="Change billable job"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ) : (
                  <AsyncSearchableSelect
                    value={billableToJobId}
                    onChange={(val) => {
                      onFieldChange('billable_to_job_id', val);
                      // The billable target IS the job link — seed it so the
                      // billable gate (customer + job) can resolve.
                      if (val && !linkedJobId) {
                        onFieldChange('linked_job_id', val);
                        const job = billableJobsCache.find(j => j.id === val);
                        if (job) onFieldChange('linked_job_name', `${job.item_number} — ${job.title}`);
                      }
                    }}
                    onSearch={async (term) => {
                      const token = authService.getTokens()?.access_token;
                      if (!token) return [];
                      const params = new URLSearchParams({ item_type: 'job', status: 'active,in_progress,open,pending', limit: '500' });
                      if (term.trim()) params.set('search', term);
                      const res = await fetch(`/api/v1/work-items?${params}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) return [];
                      const data = await res.json();
                      const jobs = (data.data || []).map((j: Record<string, unknown>) => ({
                        id: String(j.id || ''),
                        item_number: String(j.item_number || ''),
                        title: String(j.title || ''),
                        status: String(j.status || 'active'),
                      }));
                      setBillableJobsCache(jobs);
                      return jobs.map((job: { id: string; item_number: string; title: string; status: string }) => ({
                        value: job.id,
                        label: `${job.item_number} — ${job.title}`,
                        description: job.status,
                        icon: <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                      }));
                    }}
                    disabled={!isPending}
                    isDark={isDarkMode}
                    placeholder="Search job to bill to..."
                    searchPlaceholder="Search jobs..."
                    emptyMessage="No jobs found"
                    idleMessage="Start typing to search jobs..."
                    idPrefix={`billable-job-select-${index}`}
                    selectedDisplay={(() => {
                      const job = billableJobsCache.find(j => j.id === billableToJobId);
                      return job ? `${job.item_number} — ${job.title}` : billableToJobId;
                    })()}
                    selectedIcon={<Briefcase className="w-4 h-4 shrink-0 text-green-600" />}
                    className="!px-2 !py-1.5"
                  />
                )}
              </div>
              <div className="min-w-[120px]">
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Markup %
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="0.01"
                    value={markupPercentage}
                    disabled={!isPending}
                    onChange={(e) => onFieldChange('markup_percentage', String(Number(e.target.value) || 0))}
                    className={`w-full rounded-lg border pr-6 px-2 py-1.5 text-sm disabled:opacity-60 ${isDarkMode ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  />
                  <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>%</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
